import React, { Fragment, useEffect, useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  styled,
} from '@mui/material';
import { Visibility, VisibilityOutlined } from '@mui/icons-material';
import theme from '../../theme';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css'

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const CommonFormField = ({
  label,
  type = 'text',
  name,
  value,
  onChange,
  onBlur,
  placeholder,
  error,
  showPassword = false,
  onTogglePassword,
  isPhone = false,
  multiline,
  minRows,
  isLetter,
  disabled,
  width,
  onKeyDown,
  minDate,
  maxDate
}) => {
  const [displayText, setDisplayText] = useState('');

  // Convert YYYY-MM-DD to DD - MM - YYYY for display
  useEffect(() => {
    if (value) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        setDisplayText(`${month} - ${day} - ${year}`);
      } else {
        setDisplayText('');
      }
    } else {
      setDisplayText('');
    }
  }, [value]);

  const openDatePicker = () => {
    const input = document.querySelector(`input[name="${name}"]`);
    if (input && input.showPicker) {
      input.showPicker();
    } else if (input) {
      input.click();
      input.focus();
    }
  };

  const renderPhoneField = () => (
    <PhoneInput
      country={'us'}
      value={value}
      onChange={phone => onChange({ phone })}
      placeholder={placeholder}
      disabled={disabled}
      containerStyle={{ width: '100%' }}
      inputStyle={{
        width: '100%',
        height: '40px',
        fontSize: '16px',
        borderRadius: '8px'
      }}
      buttonStyle={{
        borderRadius: '8px 0 0 8px'
      }}
      dropdownStyle={{
        borderRadius: '8px',
      }}
    />
  );

  const renderPasswordField = () => (
    <TextField
      fullWidth
      placeholder={placeholder || "•••••••••"}
      name={name}
      type={showPassword ? 'text' : 'password'}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      disabled={disabled}
      size="small"
      autoComplete="new-password"
      sx={{
        '& .MuiOutlinedInput-root': {
          '&:hover fieldset': {
            border: '1px solid lightgray',
          },
          '&.Mui-focused fieldset': {
            border: '1px solid lightgray',
          },
          height: '40px',
          '& input': {
            padding: '8px 12px',
            fontSize: '14px',
          },
          '& input::placeholder': {
            letterSpacing: isLetter ? '0' : '0.2em',
            fontSize: isLetter ? '14px' : '18px',
            color: 'black',
          },
        },
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              onClick={onTogglePassword}
              edge="end"
              size="small"
            >
              {showPassword ?
                <Visibility sx={{ color: theme.palette.secondary.main }} /> :
                <VisibilityOutlined />
              }
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );

  const renderDateField = () => (
    <div style={{ position: 'relative', width: width || '100%' }}>
      <TextField
        fullWidth
        type="date"
        name={name}
        value={value || ''}
        onChange={onChange}
        size="small"
        disabled={disabled}
        sx={{
          '& .MuiOutlinedInput-root': {
            height: '40px',
            color: disabled ? 'lightgray' : 'black',
            backgroundColor: 'white',
            '& input': {
              padding: '8px 12px',
              fontSize: '14px',
              opacity: 0, // Hide native value but keep input functional
              cursor: 'pointer',
              '&::-webkit-calendar-picker-indicator': {
                position: 'absolute',
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
                width: '100%',
                height: '100%',
                cursor: 'pointer',
                opacity: 0,
              },
            },
            '& fieldset': {
              border: '1px solid lightgray !important', // Force border to show
            },
            '&:hover fieldset': {
              border: '1px solid lightgray !important',
            },
            '&.Mui-focused fieldset': {
              border: '1px solid lightgray !important',
            },
          },
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" sx={{ zIndex: 2, position: 'relative' }}>
              <IconButton
                size="small"
                sx={{
                  mr: -0.5,
                  zIndex: 3,
                  position: 'relative',
                }}
                onClick={() => {
                  if (!disabled) {
                    openDatePicker()
                  }
                }}
              >
                <img
                  src='/Images/Calendar.png'
                  style={{
                    width: 20,
                    position: 'relative'
                  }}
                  alt=''
                />
              </IconButton>
            </InputAdornment>
          ),
        }}
        inputProps={{
          ...(minDate && { min: minDate }),  // Only set min if minDate prop is passed
          ...(maxDate && { max: maxDate }),
        }}
      />

      <div
        onClick={openDatePicker}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          paddingLeft: '12px',
          paddingRight: '40px',
          fontSize: '14px',
          color: displayText ? 'inherit' : disabled ? 'lightgray' : '#6B7280',
          cursor: 'pointer',
          backgroundColor: 'transparent', // Changed from white to transparent
          pointerEvents: 'none', // Allow clicks to pass through to the input
          zIndex: 1,
          border: '1px solid transparent', // Reserve space for border
        }}
      >
        {displayText || placeholder}
      </div>
    </div>
  );

  const renderEmailField = () => (
    <TextField
      fullWidth
      placeholder={placeholder || "Please enter your email"}
      name={name}
      type="text"
      value={value}
      onChange={onChange}
      onKeyDown={(e) => {
        if (onKeyDown) {
          onKeyDown(e);
        }
      }}
      onBlur={onBlur}
      size="small"
      multiline={multiline}
      minRows={minRows || 5}
      disabled={disabled}
      sx={{
        '& .MuiOutlinedInput-root': {
          '&:hover fieldset': {
            border: '1px solid lightgray',
          },
          '&.Mui-focused fieldset': {
            border: '1px solid lightgray',
          },
          height: multiline ? 'auto' : '40px',
          background: 'white',
          '& input': {
            padding: '8px 12px',
            fontSize: '14px',
          },
        },
      }}
    />
  );

  const renderNumberField = () => (
    <TextField
      fullWidth
      placeholder={placeholder || "Enter phone number"}
      name={name}
      type="number"
      value={value}
      onChange={(e) => {
        const inputValue = e.target.value;
        // Only allow positive numbers and empty string
        if (inputValue === '' || parseInt(inputValue) >= 0) {
          onChange(e);
        }
      }}
      onBlur={onBlur}
      size="small"
      disabled={disabled}
      onWheel={(e) => e.target.blur()}
      onKeyDown={(e) => {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
          e.preventDefault();
        }
        // Prevent minus key
        if (e.key === '-' || e.key === 'Minus') {
          e.preventDefault();
        }
      }}
      inputProps={{
        style: {
          MozAppearance: 'textfield',
          WebkitAppearance: 'none',
          appearance: 'none',
        }
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          '&:hover fieldset': {
            border: '1px solid lightgray',
          },
          '&.Mui-focused fieldset': {
            border: '1px solid lightgray',
          },
          height: '38px',
          '& input': {
            padding: '8px 0px 8px 8px', // Less left padding
            fontSize: '14px',
            '&[type=number]': {
              MozAppearance: 'textfield',
            },
            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
              WebkitAppearance: 'none',
              margin: 0,
            },
          },
        },
      }}
    />
  );
  const renderFileField = () => (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <TextField
        fullWidth
        placeholder={placeholder}
        name="file"
        type="text"
        value={value}
        size="small"
        disabled
        sx={{
          '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
              border: '1px solid lightgray',
            },
            '&.Mui-focused fieldset': {
              border: '1px solid lightgray',
            },
            height: '38px',
            '& input': {
              padding: '8px 0px 8px 8px', // Less left padding
              fontSize: '14px',
            },
          },
        }}
      />
      <IconButton
        component="label"
        style={{
          padding: '5px 10px 5px 10px',
          border: '1px solid lightGray',
          borderRadius: 8
        }}
      >
        <img src='/Images/Upload.png' alt='upload' />
        <VisuallyHiddenInput
          type="file"
          name={name}
          multiple
          accept='.pdf'
          onChange={(event) => onChange(event)}
        />
      </IconButton>
    </Box>
  );

  return (
    <Fragment>
      {label && (
        <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 500, color: 'text.main', mb: 0.8 }}>
          {label}
        </Typography>
      )}

      {isPhone ? renderPhoneField() :
        type === 'password' ? renderPasswordField() :
          type === 'date' ? renderDateField() :
            type === 'number' ? renderNumberField() :
              type === 'file' ? renderFileField() :
                renderEmailField()}

      {error && (
        <Typography variant="caption" color="error" sx={{ fontSize: '12px', display: 'block' }}>
          {error}
        </Typography>
      )}
    </Fragment>
  );
};

export default CommonFormField;